// src/services/RobustSupabaseClient.ts
/**
 * Robust Supabase Client Wrapper
 * Adds retry logic, circuit breakers, caching, and better error handling
 */

import { supabase } from '../config/supabase';
import { ErrorLoggerService } from './ErrorLoggerService';
import { robustRequest, getCircuitBreaker, CircuitState } from './RobustnessUtils';

// ==================== TYPES ====================

export interface RobustQueryOptions {
  /** Enable retry with exponential backoff (default: true) */
  enableRetry?: boolean;
  /** Maximum retry attempts (default: 3) */
  maxRetries?: number;
  /** Request timeout in ms (default: 10000) */
  timeoutMs?: number;
  /** Enable circuit breaker (default: true for write operations) */
  enableCircuitBreaker?: boolean;
  /** Circuit breaker name (default: operation-specific) */
  circuitBreakerName?: string;
  /** Enable response caching (default: false) */
  enableCache?: boolean;
  /** Cache TTL in ms (default: 60000 = 1 minute) */
  cacheTtlMs?: number;
  /** Custom cache key (auto-generated if not provided) */
  cacheKey?: string;
  /** Component name for error logging */
  componentName?: string;
  /** Action name for error logging */
  actionName?: string;
}

// ==================== MAIN CLASS ====================

export class RobustSupabaseClient {

  /**
   * Execute a SELECT query with robustness features
   */
  static async select<T>(
    table: string,
    query: (builder: any) => any,
    options: RobustQueryOptions = {}
  ): Promise<{ data: T[] | null; error: any }> {
    const {
      enableRetry = true,
      maxRetries = 3,
      timeoutMs = 10000,
      enableCircuitBreaker = false,
      circuitBreakerName = `supabase-select-${table}`,
      enableCache = false,
      cacheTtlMs = 60000,
      cacheKey,
      componentName = 'RobustSupabaseClient',
      actionName = `select-${table}`
    } = options;

    const actualCacheKey = cacheKey || `select:${table}:${JSON.stringify(query)}`;

    try {
      const result = await robustRequest(
        async () => {
          const builder = supabase.from(table).select();
          const finalBuilder = query(builder);
          const { data, error } = await finalBuilder;

          if (error) {
            throw new Error(`Database query failed: ${error.message}`);
          }

          return { data, error: null };
        },
        {
          maxAttempts: enableRetry ? maxRetries : 1,
          delayMs: 1000,
          backoffMultiplier: 2,
          maxDelayMs: 5000,
          timeoutMs,
          circuitBreakerName: enableCircuitBreaker ? circuitBreakerName : undefined,
          cacheKey: enableCache ? actualCacheKey : undefined,
          cacheTtlMs: enableCache ? cacheTtlMs : undefined,
          onRetry: (attempt, error) => {
            console.warn(`[RobustSupabase] SELECT retry ${attempt} for table ${table}:`, error);
          }
        }
      );

      return result;

    } catch (error: any) {
      // Log only on final failure (after all retries exhausted)
      await ErrorLoggerService.logDatabaseError(error.message, table, 'SELECT', componentName);
      return { data: null, error };
    }
  }

  /**
   * Execute an INSERT query with robustness features
   */
  static async insert<T>(
    table: string,
    data: any,
    options: RobustQueryOptions = {}
  ): Promise<{ data: T | null; error: any }> {
    const {
      enableRetry = true,
      maxRetries = 2, // Less retries for mutations
      timeoutMs = 15000, // Longer timeout for writes
      enableCircuitBreaker = true, // Enable by default for writes
      circuitBreakerName = `supabase-insert-${table}`,
      componentName = 'RobustSupabaseClient',
      actionName = `insert-${table}`
    } = options;

    try {
      const result = await robustRequest(
        async () => {
          // Check if data is an array (batch insert) or single object
          const isBatch = Array.isArray(data);

          let query = supabase
            .from(table)
            .insert(data)
            .select();

          // Only use .single() for single inserts, NOT for batch inserts
          let finalQuery: any = query;
          if (!isBatch) {
            finalQuery = query.single();
          }

          const { data: insertedData, error } = await finalQuery;

          if (error) {
            throw new Error(`Database insert failed: ${error.message}`);
          }

          return { data: insertedData, error: null };
        },
        {
          maxAttempts: enableRetry ? maxRetries : 1,
          delayMs: 1000,
          backoffMultiplier: 2,
          maxDelayMs: 5000,
          timeoutMs,
          circuitBreakerName: enableCircuitBreaker ? circuitBreakerName : undefined,
          onRetry: (attempt, error) => {
            console.warn(`[RobustSupabase] INSERT retry ${attempt} for table ${table}:`, error);
          }
        }
      );

      return result;

    } catch (error: any) {
      await ErrorLoggerService.logDatabaseError(error.message, table, 'INSERT', componentName);
      return { data: null, error };
    }
  }

  /**
   * Execute an UPDATE query with robustness features
   */
  static async update<T>(
    table: string,
    data: any,
    match: Record<string, any>,
    options: RobustQueryOptions = {}
  ): Promise<{ data: T[] | null; error: any }> {
    const {
      enableRetry = true,
      maxRetries = 2,
      timeoutMs = 15000,
      enableCircuitBreaker = true,
      circuitBreakerName = `supabase-update-${table}`,
      componentName = 'RobustSupabaseClient',
      actionName = `update-${table}`
    } = options;

    try {
      const result = await robustRequest(
        async () => {
          let query = supabase.from(table).update(data);

          // Apply match conditions
          for (const [key, value] of Object.entries(match)) {
            query = query.eq(key, value);
          }

          const { data: updatedData, error } = await query.select();

          if (error) {
            throw new Error(`Database update failed: ${error.message}`);
          }

          return { data: updatedData, error: null };
        },
        {
          maxAttempts: enableRetry ? maxRetries : 1,
          delayMs: 1000,
          backoffMultiplier: 2,
          maxDelayMs: 5000,
          timeoutMs,
          circuitBreakerName: enableCircuitBreaker ? circuitBreakerName : undefined,
          onRetry: (attempt, error) => {
            console.warn(`[RobustSupabase] UPDATE retry ${attempt} for table ${table}:`, error);
          }
        }
      );

      return result;

    } catch (error: any) {
      await ErrorLoggerService.logDatabaseError(error.message, table, 'UPDATE', componentName);
      return { data: null, error };
    }
  }

  /**
   * Execute a DELETE query with robustness features
   */
  static async delete(
    table: string,
    match: Record<string, any>,
    options: RobustQueryOptions = {}
  ): Promise<{ error: any }> {
    const {
      enableRetry = true,
      maxRetries = 2,
      timeoutMs = 15000,
      enableCircuitBreaker = true,
      circuitBreakerName = `supabase-delete-${table}`,
      componentName = 'RobustSupabaseClient',
      actionName = `delete-${table}`
    } = options;

    try {
      const result = await robustRequest(
        async () => {
          let query = supabase.from(table).delete();

          // Apply match conditions
          for (const [key, value] of Object.entries(match)) {
            query = query.eq(key, value);
          }

          const { error } = await query;

          if (error) {
            throw new Error(`Database delete failed: ${error.message}`);
          }

          return { error: null };
        },
        {
          maxAttempts: enableRetry ? maxRetries : 1,
          delayMs: 1000,
          backoffMultiplier: 2,
          maxDelayMs: 5000,
          timeoutMs,
          circuitBreakerName: enableCircuitBreaker ? circuitBreakerName : undefined,
          onRetry: (attempt, error) => {
            console.warn(`[RobustSupabase] DELETE retry ${attempt} for table ${table}:`, error);
          }
        }
      );

      return result;

    } catch (error: any) {
      await ErrorLoggerService.logDatabaseError(error.message, table, 'DELETE', componentName);
      return { error };
    }
  }

  /**
   * Execute an RPC (Remote Procedure Call) with robustness features
   */
  static async rpc<T>(
    functionName: string,
    params?: Record<string, any>,
    options: RobustQueryOptions = {}
  ): Promise<{ data: T | null; error: any }> {
    const {
      enableRetry = true,
      maxRetries = 3,
      timeoutMs = 20000, // Longer timeout for RPC
      enableCircuitBreaker = true,
      circuitBreakerName = `supabase-rpc-${functionName}`,
      enableCache = false,
      cacheTtlMs = 60000,
      cacheKey,
      componentName = 'RobustSupabaseClient',
      actionName = `rpc-${functionName}`
    } = options;

    const actualCacheKey = cacheKey || `rpc:${functionName}:${JSON.stringify(params)}`;

    try {
      const result = await robustRequest(
        async () => {
          const { data, error } = await supabase.rpc(functionName, params);

          if (error) {
            throw new Error(`RPC ${functionName} failed: ${error.message}`);
          }

          return { data, error: null };
        },
        {
          maxAttempts: enableRetry ? maxRetries : 1,
          delayMs: 1000,
          backoffMultiplier: 2,
          maxDelayMs: 5000,
          timeoutMs,
          circuitBreakerName: enableCircuitBreaker ? circuitBreakerName : undefined,
          cacheKey: enableCache ? actualCacheKey : undefined,
          cacheTtlMs: enableCache ? cacheTtlMs : undefined,
          deduplicationKey: actualCacheKey, // Always deduplicate RPC calls
          onRetry: (attempt, error) => {
            console.warn(`[RobustSupabase] RPC ${functionName} retry ${attempt}:`, error);
          }
        }
      );

      return result;

    } catch (error: any) {
      await ErrorLoggerService.logDatabaseError(error.message, functionName, 'RPC', componentName);
      return { data: null, error };
    }
  }

  /**
   * Get circuit breaker status for a service
   */
  static getCircuitBreakerStatus(serviceName: string): CircuitState {
    return getCircuitBreaker(serviceName).getState();
  }

  /**
   * Reset a circuit breaker (admin operation)
   */
  static resetCircuitBreaker(serviceName: string): void {
    getCircuitBreaker(serviceName).reset();
    console.log(`[RobustSupabase] Circuit breaker reset: ${serviceName}`);
  }
}
